/**
 * JS-side handle for the custom `BgTracker` Capacitor plugin (Android
 * foreground-service GPS + iOS Always-authorization CoreLocation).
 *
 * WHY A CUSTOM PLUGIN AND NOT `@capacitor/geolocation`:
 * `@capacitor/geolocation`'s `watchPosition` is a plain WHILE-IN-USE
 * watcher on both platforms — the OS is free to suspend it the moment the
 * app is backgrounded (screen lock, app switch, incoming call). That is
 * `driver-tracker.ts`'s pre-2026-09-22 behaviour and the reason a bus froze
 * on every parent's map the moment a driver's screen locked. There is no
 * config flag on that plugin that changes this; the fix requires native
 * code neither plugin exposes:
 *   - Android: a **foreground service** (`android:foregroundServiceType=
 *     "location"`) with a persistent notification, which is the ONLY
 *     Android-sanctioned way to keep receiving location callbacks while
 *     backgrounded without the heavier `ACCESS_BACKGROUND_LOCATION`
 *     permission (see BgTrackerService.java for why that permission is
 *     deliberately NOT requested).
 *   - iOS: `CLLocationManager.allowsBackgroundLocationUpdates = true`,
 *     which requires "Always" authorization and is not something
 *     `@capacitor/geolocation`'s Swift implementation sets.
 *
 * Native source: `native-overrides/android/BgTrackerPlugin.java` +
 * `BgTrackerService.java`, `native-overrides/ios/BgTrackerPlugin.swift`.
 * Registered as plugin id `BgTracker` in both `MainActivity.java`
 * (Android) and Capacitor's Swift plugin auto-registration (iOS, via
 * `CAPBridgedPlugin` conformance — no manual registration file needed).
 *
 * `@capacitor/geolocation` is still used elsewhere (one-shot
 * `getCurrentPosition` for the manual test-ping and attendance geofence
 * check-ins) — those only ever run in the foreground, so the OS suspension
 * problem doesn't apply to them and swapping plugins there would be
 * needless churn.
 */
import { registerPlugin } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

/** Wire shape of one native fix, as emitted by the "fix" event. */
export interface BgTrackerFix {
  latitude: number;
  longitude: number;
  /** Metres/second. `null` when the OS reports no usable speed (e.g. cold fix). */
  speedMps: number | null;
  /** Degrees, 0-360. `null` when the OS reports no usable heading. */
  headingDeg: number | null;
  /** Reported horizontal accuracy in metres. `null` when unknown. */
  accuracyM: number | null;
  /** Fix capture time, epoch ms — the DEVICE's clock, not receipt time. */
  timestampMs: number;
}

export type BgTrackerPermState = "granted" | "denied" | "prompt";

export interface BgTrackerPermissionStatus {
  /** Foreground (FINE/COARSE on Android, When-In-Use on iOS). */
  location: BgTrackerPermState;
  /**
   * Background delivery permission.
   *   - Android: always "granted" once `location` is — the foreground
   *     service is what earns background delivery there, not a distinct
   *     OS permission (see BgTrackerService.java).
   *   - iOS: "granted" only at CLAuthorizationStatus.authorizedAlways.
   *     "denied" (not "prompt") when only When-In-Use is held, because
   *     re-requesting is a Settings trip, not another in-app dialog.
   */
  backgroundLocation: BgTrackerPermState;
}

export interface BgTrackerStartOptions {
  /** Persistent Android notification title (required by Play policy: must
   *  state what's happening, not be generic "App is running"). */
  notificationTitle: string;
  notificationText: string;
  /** Upload-relevant cadence while the bus is moving. */
  movingIntervalMs: number;
  /** Backed-off cadence while the bus is stationary — the battery saver. */
  stationaryIntervalMs: number;
  /** Speed (m/s) below which the bus counts as "stationary" after a few
   *  consecutive fixes under it. */
  stationarySpeedMps: number;
}

export interface BgTrackerPlugin {
  checkPermissions(): Promise<BgTrackerPermissionStatus>;
  /** Prompts for foreground location if not yet determined; otherwise
   *  resolves immediately with the current status. */
  requestForegroundPermission(): Promise<BgTrackerPermissionStatus>;
  /**
   * Best-effort upgrade to "Always" (iOS) / no-op (Android, see above).
   * MUST be called only after `location` is already granted — requesting
   * background before foreground is refused by both platforms. Resolves
   * with the status BEFORE any OS upgrade dialog is answered (iOS's
   * upgrade prompt does not reliably fire a completion callback) — callers
   * should re-run `checkPermissions()` on next foreground resume to read
   * the real outcome instead of awaiting this call for it.
   */
  requestBackgroundPermission(): Promise<BgTrackerPermissionStatus>;
  start(options: BgTrackerStartOptions): Promise<{ started: boolean }>;
  stop(): Promise<void>;
  /**
   * Android only, best-effort, non-blocking. Opens the OS
   * "ignore battery optimizations" dialog for this app — the standard
   * mitigation for OEM aggressive-kill battery managers (MIUI, ColorOS,
   * OneUI) that terminate a foreground service's process regardless of the
   * foreground-service protections. A no-op that resolves immediately on
   * iOS (no such concept) and on Android versions/OEMs where the app is
   * already exempted.
   */
  requestBatteryOptimizationExemption(): Promise<{ alreadyExempt: boolean }>;
  addListener(
    eventName: "fix",
    listenerFunc: (fix: BgTrackerFix) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "error",
    listenerFunc: (err: { message: string }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  removeAllListeners(): Promise<void>;
}

// No web implementation object: this plugin only ever runs under
// `isNative()` guards (see driver-tracker.ts). Calling it on web rejects
// with Capacitor's standard "not implemented on web" error, which is the
// correct failure mode for a call site that should never be reached.
export const BgTracker = registerPlugin<BgTrackerPlugin>("BgTracker");
